import { useEffect, useState, useCallback } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X, DeviceMobile } from '@phosphor-icons/react'
import BrandLogo from './website/BrandLogo'
import ShimmerButton from './magicui/ShimmerButton'

/**
 * PWAInstallPrompt — custom React banner for the `beforeinstallprompt`
 * flow on Android Chrome / Edge / Samsung Internet.
 *
 * UX rules (premium, non-intrusive):
 *   1. Never shown if the user already launched the PWA standalone.
 *   2. Never shown within 14 days of a dismissal (localStorage cooldown).
 *   3. Never shown if the browser never fires `beforeinstallprompt`
 *      (Safari, older WebKit — Safari users get the "Add to Home Screen"
 *      instruction via the iOS splash path instead).
 *   4. Mobile: bottom-anchored sheet-style card above the tab bar.
 *   5. Desktop: bottom-right toast-style card (sits below UpdateAvailableModal
 *      which lives top-right, so they never collide).
 *   6. Respects `prefers-reduced-motion` — fade-only entrance in that case.
 *   7. Accessibility: role="dialog", aria-labelledby, focusable buttons.
 *
 * The underlying `beforeinstallprompt` event is a `BeforeInstallPromptEvent`;
 * we keep the instance in state + call `.prompt()` on CTA click. After the
 * user accepts or dismisses the browser dialog, we unmount.
 */

// BeforeInstallPromptEvent isn't in lib.dom yet (still Working Draft).
// This is the spec shape per Chrome docs.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const DISMISS_KEY = 'bc_pwa_install_dismissed_at'
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const mq = window.matchMedia?.('(display-mode: standalone)')
  // iOS Safari also exposes navigator.standalone on the home-screened app
  const iosStandalone = Boolean((window.navigator as { standalone?: boolean }).standalone)
  return Boolean(mq?.matches) || iosStandalone
}

function isWithinCooldown(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Number.parseInt(raw, 10)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts < COOLDOWN_MS
  } catch {
    return false
  }
}

export default function PWAInstallPrompt() {
  const { t } = useTranslation('components')
  const reducedMotion = useReducedMotion() ?? false
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Absolute gates before we even listen.
    if (isStandalone()) return
    if (isWithinCooldown()) return

    const handler = (e: Event) => {
      // Prevent Chrome's own mini-infobar — we render our own.
      e.preventDefault()
      setInstallEvent(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handler as EventListener)
    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener)
  }, [])

  // If the app gets installed while we're mounted (e.g. user triggered
  // install via the omnibox), hide the banner.
  useEffect(() => {
    const onInstalled = () => setInstallEvent(null)
    window.addEventListener('appinstalled', onInstalled)
    return () => window.removeEventListener('appinstalled', onInstalled)
  }, [])

  const handleInstall = useCallback(async () => {
    if (!installEvent) return
    try {
      await installEvent.prompt()
      await installEvent.userChoice
    } catch {
      // User cancelled the native dialog — nothing to recover.
    } finally {
      setInstallEvent(null)
    }
  }, [installEvent])

  const handleDismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      /* quota / private-mode — non-fatal */
    }
    setDismissed(true)
  }, [])

  const visible = installEvent !== null && !dismissed

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-labelledby="pwa-install-title"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
          transition={reducedMotion ? { duration: 0.2 } : { type: 'spring', stiffness: 380, damping: 32, mass: 0.9 }}
          className="fixed z-[60] left-3 right-3 bottom-[calc(72px+env(safe-area-inset-bottom))] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-[380px]"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0f0f18]/96 shadow-[0_24px_80px_-20px_rgba(139,92,246,0.45)] backdrop-blur-2xl light:border-gray-200 light:bg-white light:shadow-[0_24px_80px_-20px_rgba(139,92,246,0.25)]">
            <button
              type="button"
              onClick={handleDismiss}
              aria-label={t('pwa.install.dismiss')}
              className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-lg text-[var(--bn-text-subtle)] transition-colors hover:bg-white/5 hover:text-zinc-300 light:text-[var(--bn-text-muted)] light:hover:bg-gray-100 light:hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" weight="bold" />
            </button>

            <div className="px-5 py-5">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--bn-accent-bg-active)] border border-purple-500/25">
                  <DeviceMobile className="h-4 w-4 text-[var(--bn-accent)]" weight="duotone" />
                </div>
                <BrandLogo
                  variant="inline"
                  className="text-sm tracking-[0.01em] text-zinc-300 light:text-gray-700"
                />
              </div>

              <h3
                id="pwa-install-title"
                className="text-base font-light tracking-tight text-white light:text-gray-900"
              >
                {t('pwa.install.title')}
              </h3>

              <p className="mt-1.5 text-[13px] font-light leading-relaxed text-[var(--bn-text-muted)] light:text-gray-600">
                {t('pwa.install.subtitle')}
              </p>

              <div className="mt-4 flex items-center gap-2">
                <ShimmerButton
                  type="button"
                  onClick={handleInstall}
                  className="!px-4 !py-2.5 text-xs"
                >
                  {t('pwa.install.cta')}
                </ShimmerButton>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="rounded-lg px-3 py-2 text-xs font-medium text-[var(--bn-text-subtle)] transition-colors hover:bg-white/5 hover:text-zinc-300 light:text-[var(--bn-text-subtle)] light:hover:bg-gray-100 light:hover:text-gray-800"
                >
                  {t('pwa.install.later')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
